import dayjs from 'dayjs';
import { removeCommaSeparator, noop } from '@ajaibid/utils';
import {
  postStockBuy,
  postStockSell,
  postStockBonusSell,
  postStockAutoOrder,
} from '@services/api/stock/trading';
import { postMtBuy, postMtSell, postMtAutoOrder } from '@services/api/stock/marginTrading';
import { stockCalculateTotal } from '@helpers/stock';
import { eventClickSellConfirm, eventClickBuyConfirm } from './tracker';

import {
  INPUT_PRICE,
  INPUT_LOT,
  INPUT_PRICE_AUTO_LIMIT,
  INPUT_LOT_AUTO,
  INPUT_PRICE_AUTO,
  TYPE_BUY,
  TYPE_SELL,
  TYPE_SELL_BONUS,
} from './constants';

/**
 *
 * @param {Object} formValues
 * @returns Object
 */
export const getFormValues = (formValues = {}) => {
  const lotForm = formValues[INPUT_LOT]?.value;
  const lotInt = parseInt(removeCommaSeparator(lotForm), 10);
  const priceForm = formValues[INPUT_PRICE]?.value;
  const priceInt = parseInt(removeCommaSeparator(priceForm), 10);
  const priceLimitForm = formValues[INPUT_PRICE_AUTO_LIMIT]?.value;
  const priceLimitInt = parseInt(removeCommaSeparator(priceLimitForm), 10);

  const lotAutoForm = formValues[INPUT_LOT_AUTO]?.value;
  const lotAutoInt = parseInt(removeCommaSeparator(lotAutoForm), 10);
  const priceAutoForm = formValues[INPUT_PRICE_AUTO]?.value;
  const priceAutoInt = parseInt(removeCommaSeparator(priceAutoForm), 10);

  const totalLimit = stockCalculateTotal(priceInt, lotInt);
  const totalAuto = stockCalculateTotal(priceAutoInt, lotAutoInt);

  return {
    lotInt,
    lotForm,
    lotAutoInt,
    lotAutoForm,
    priceInt,
    priceForm,
    priceAutoInt,
    priceAutoForm,
    priceLimitInt,
    priceLimitForm,
    totalLimit,
    totalAuto,
  };
};

const replaceBonus = (code) => code.replace('BONUS-', '');

/**
 *
 * @param {[object]} data
 * @param {string} selectedCode
 * @param {string} key
 * @param {boolean} isSearch
 * @returns array of object of code list for SelectFilter
 */
export const getCodeList = (data, selectedCode, key = 'code', isSearch = false) => {
  const selectedData = { text: selectedCode, value: selectedCode };

  if (!data) return [selectedData];

  const codeAvailable = data.some(
    (res) => res[key] === selectedCode || replaceBonus(res[key]) === selectedCode
  );
  const list = data.map((res) => ({
    text: replaceBonus(res[key]),
    value: replaceBonus(res[key]),
  }));

  if (!codeAvailable && !isSearch) list.unshift(selectedData);

  return list;
};

/**
 *
 * @param {[object]} portfolio
 * @param {string} selectedCode
 * @param {string} key
 * @returns number of lot
 */
export const getAvailableLot = (portfolio, selectedCode, key = 'code') => {
  if (!portfolio) return 0;
  if (typeof portfolio === 'object' && !portfolio.length) return 0;

  return portfolio?.reduce((a, b) => {
    const code = b[key];
    if (code === selectedCode || code === `BONUS-${selectedCode}`) {
      return a + b.lot;
    }
    return a;
  }, 0);
};

export const getPayload = (data) => {
  const {
    autoStartDate,
    autoEndDate,
    autoPriceCondition,
    form,
    selectedAccountType,
    selectedCode,
    selectedPeriod,
    tabOrder,
    typeOrder,
  } = data;
  const used = {
    limit: {
      price: form.priceInt,
      lot: form.lotInt,
    },
    auto: {
      price: form.priceAutoInt,
      lot: form.lotAutoInt,
    },
  };
  let payload = {};
  let trackEvent = noop;
  let usedFetch = noop;

  const mtSelected = selectedAccountType === 'margin';
  const isAuto = tabOrder === 'auto';
  const isBuy = typeOrder === TYPE_BUY;
  const isSell = typeOrder === TYPE_SELL;
  const isSellBonus = typeOrder === TYPE_SELL_BONUS;

  if (isAuto) {
    payload = {
      code: selectedCode,
      side: isBuy ? 'BUY' : 'SELL',
      criterion: 'PRICE',
      comparator: autoPriceCondition === 0 ? 'LTE' : 'GTE',
      value: form.priceLimitInt,
      order_price: used[tabOrder].price,
      lot: used[tabOrder].lot,
      start_date: dayjs(autoStartDate).format('YYYY-MM-DD'),
      end_date: dayjs(autoEndDate).format('YYYY-MM-DD'),
      expiry: 'day',
    };
    usedFetch = mtSelected ? postMtAutoOrder : postStockAutoOrder;
    trackEvent = isBuy ? eventClickBuyConfirm : eventClickSellConfirm;
  } else {
    payload = {
      ticker_code: selectedCode,
      price: used[tabOrder].price,
      lot: used[tabOrder].lot,
      board: '0RG',
      period: selectedPeriod,
    };
  }

  if (isSellBonus && !isAuto) {
    usedFetch = postStockBonusSell;
    payload.code = `BONUS-${selectedCode}`;
    payload.price = used[tabOrder].price;
    payload.lot = used[tabOrder].lot;
  } else if (mtSelected && isBuy && !isAuto) {
    usedFetch = postMtBuy;
    trackEvent = eventClickBuyConfirm;
  } else if (mtSelected && isSell && !isAuto) {
    usedFetch = postMtSell;
    trackEvent = eventClickSellConfirm;
  } else if (!mtSelected && isBuy && !isAuto) {
    usedFetch = postStockBuy;
    trackEvent = eventClickBuyConfirm;
  } else if (!mtSelected && isSell && !isAuto) {
    usedFetch = postStockSell;
    trackEvent = eventClickSellConfirm;
  }

  return {
    trackEvent,
    payload,
    postData: usedFetch,
  };
};
